/**
 * Value Transformer - Filter value transformation logic
 *
 * Transforms filter values based on field type and entity context,
 * handling special cases like status codes, severity mapping, and boolean fields.
 *
 * @module queryBuilderCore/transformers/valueTransformer
 */

import { mapSeverityToPriority } from '@/utils/mappings.util';

/**
 * Transforms filter values based on field and entity type
 *
 * @param field - Field name being filtered
 * @param value - Original filter value(s)
 * @param entityType - Type of entity (Asset, Vulnerability, etc.)
 * @returns Transformed value(s) appropriate for the field type
 */
export function transformFilterValue(
  field: string,
  value: string | string[] | number | number[] | boolean | boolean[],
  entityType: string
): string | string[] | number | number[] | boolean | boolean[] {
  if (field === 'status' && entityType === 'Vulnerability') {
    return value;
  }

  if (field === 'priority') {
    let stringValue: string | string[];
    if (Array.isArray(value)) {
      stringValue = value.map(v => String(v));
    } else {
      stringValue = String(value);
    }
    return mapSeverityToPriority(stringValue);
  }

  if (
    [
      'kev',
      'exploit',
      'admincount',
      'isaclprotected',
      'pwdneverexpires',
      'isdc',
      'unconstraineddelegation',
      'ldapavailable',
      'ldapsavailable',
      'authenticationenabled',
      'adminsdholderprotected',
      'blocksinheritance',
      'dontreqpreauth',
      'passwordexpired',
      'encryptedtextpwdallowed',
      'smartcardrequired',
      'trustedtoauth',
      'enrolleesuppliessubject',
      'requiresmanagerapproval',
      'sensitive',
    ].includes(field)
  ) {
    if (Array.isArray(value)) {
      return value.map(v => String(v) === 'true');
    } else {
      return String(value) === 'true';
    }
  }

  if (['cvss', 'epss'].includes(field)) {
    if (Array.isArray(value)) {
      return value.map(v => Number(v));
    } else if (typeof value === 'string') {
      return Number(value);
    }
  }

  return value;
}
